import { useStepper } from '@/components/Molecules/Stepper'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { CheckIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'

interface CertificateApplicationProgressProps {
  allComplete?: string | string[] // Path(s) that indicate(s) all steps are complete
  canJumpBack?: boolean // Allow jumping back to a completed step by clicking its pill
  className?: string // Additional class name for the root card component
}

const ALL_COMPLETE = 9999

export function CertificateApplicationProgress({
  allComplete,
  canJumpBack,
  className,
}: CertificateApplicationProgressProps) {
  const { pathname } = useLocation()
  const { steps, activeStep, setStep } = useStepper()
  const activeStepRef = useRef<HTMLSpanElement>(null)

  const activeIndex = (() => {
    const completeSteps = Array.isArray(allComplete)
      ? allComplete
      : [allComplete]
    if (completeSteps.some((p) => p === pathname)) return ALL_COMPLETE
    return steps.findIndex((step) => step.path === activeStep?.path)
  })()

  const isAllComplete = activeIndex === ALL_COMPLETE
  const currentStepNumber = isAllComplete ? steps.length : activeIndex + 1
  const percentage = steps.length ? (currentStepNumber / steps.length) * 100 : 0
  const nextStep = isAllComplete ? undefined : steps[activeIndex + 1]

  // Keep the current step in view on mobile, where the pills collapse to a
  // single scrollable row and the current step may start off-screen.
  useEffect(() => {
    setTimeout(
      () =>
        activeStepRef.current?.scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: 'smooth',
        }),
      300
    )
  }) // Run on every render (no effect deps)

  return (
    <Card className={cn('mx-auto max-w-full', className)}>
      <CardContent className="flex flex-col gap-3.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-bold">
            Step {currentStepNumber} of {steps.length}
          </span>
          {nextStep && (
            <span className="text-muted-foreground text-sm font-semibold">
              Next: {nextStep.title}
            </span>
          )}
        </div>

        <Progress value={percentage} />

        <div className="flex flex-nowrap items-center gap-2 overflow-x-auto lg:flex-wrap lg:overflow-visible">
          {steps.map((step, index) => {
            const isStepComplete = isAllComplete || index < activeIndex
            const isCurrent = !isAllComplete && index === activeIndex
            const variant = isStepComplete
              ? 'success'
              : isCurrent
                ? 'default'
                : 'muted'
            const content = (
              <>
                {isStepComplete && <CheckIcon />}
                {step.title}
              </>
            )

            return (
              <span
                key={step.path}
                ref={isCurrent ? activeStepRef : undefined}
                className="shrink-0"
              >
                {canJumpBack && isStepComplete && !isAllComplete ? (
                  <Badge variant={variant} className="shrink-0 text-sm" asChild>
                    <button type="button" onClick={() => setStep(steps[index])}>
                      {content}
                    </button>
                  </Badge>
                ) : (
                  <Badge variant={variant} className="shrink-0 text-sm">
                    {content}
                  </Badge>
                )}
              </span>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
